<template>
    <b-modal
        :id="modalId"
        :ref="modalId"
        title-tag="h2"
        title="Bulk Update"
        :lazy="true"
        :visible="isOpen"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-alert :show="showCertWarning" variant="warning">
            <i class="cus-error"></i>
            Generated certificates will be stored in patient Documents
        </b-alert>
        <div class="form-group row">
            <label for="applyTo" class="col-4 col-form-label">Apply to:</label>
            <div class="col-8 px-2 col-input">
                <b-select
                    v-model="data.applyTo"
                    class="form-control py-0"
                    name="applyTo"
                >
                    <option value="0">Specific Items</option>
                    <option value="1">Specific Categories</option>
                </b-select>
            </div>
        </div>
        <div class="form-group row">
            <label for="applyToItems" class="col-4 col-form-label"></label>
            <div class="col-8 px-2 col-input">
                <v-select
                    v-if="data.applyTo == 0"
                    v-model="data.applyToItems"
                    multiple
                    name="applyToItems"
                    placeholder="Item(s) (required)"
                    v-validate="'required'"
                    :class="{
                        'border-danger': errors.has('applyToItems')
                    }"
                    class="v-select-search"
                    :options="itemsFiltered"
                    :filterable="false"
                    :clearable="true"
                    :closeOnSelect="false"
                    :clearSearchOnSelect="true"
                    :selectOnTab="true"
                    @search="searchItem"
                >
                    <template v-slot:option="{ label }">
                        <b>{{ label }}</b>
                    </template>

                    <template v-slot:no-options="{ search }">
                        <div>
                            <span v-if="search === ''">
                                Type to search
                            </span>
                            <span v-else-if="optionLoading.items">
                                Loading...
                            </span>
                            <span v-else>
                                No options
                            </span>
                        </div>
                    </template>
                </v-select>
                <treeselect
                    v-else
                    v-model="data.applyToCategories"
                    :multiple="true"
                    :value-consists-of="'LEAF_PRIORITY'"
                    :default-expand-level="1"
                    :options="typesArray"
                    :searchable="true"
                    :clear-on-select="true"
                    :class="{
                        'treeselect-invalid': formError.includes(
                            'applyToCategories'
                        )
                    }"
                    @input="updateSelectedCategory"
                >
                    <label
                        style="margin-bottom: 2px;"
                        slot="option-label"
                        slot-scope="{ node }"
                    >
                        <span v-if="node.isBranch" style="font-weight: bold;">
                            {{ node.label }}
                        </span>
                        <span v-else style="font-weight: normal;">
                            {{ node.label }}
                        </span>
                    </label>
                </treeselect>
            </div>
        </div>

        <div class="form-group row">
            <label for="update" class="col-4 col-form-label">Update: </label>
            <div class="col-8 px-2 col-input">
                <b-select
                    v-model="data.update"
                    class="form-control py-0"
                    name="update"
                    v-validate="{ is_not: 0 }"
                    :class="{
                        'border-danger': errors.has('update')
                    }"
                >
                    <option :value="0"
                        >Select update to apply (required)</option
                    >
                    <option :value="UPDATES.CHANGE_CONTROLLED_STATUS"
                        >Change Controlled Status</option
                    >
                    <option :value="UPDATES.CHANGE_FEES">Change Fees</option>
                    <option :value="UPDATES.CHANGE_NEXT_DUE"
                        >Change Next Due</option
                    >
                    <option :value="UPDATES.UPDATE_POI"
                        >Change Print on Invoice Status</option
                    >
                    <option :value="UPDATES.CREATE_ACTION"
                        >Create Action</option
                    >
                    <option :value="UPDATES.INCREASE_PRICE"
                        >Increase Price</option
                    >
                    <option :value="UPDATES.REMOVE_ACTION"
                        >Remove Action</option
                    >
                    <option :value="UPDATES.SET_PRICE">Set Price</option>
                </b-select>
            </div>
        </div>
        <!-- UPDATE = 2 (CHANGE CONTROLLED STATUS) -->
        <div
            class="form-group row"
            v-if="data.update == UPDATES.CHANGE_CONTROLLED_STATUS"
        >
            <label for="control" class="col-4 col-form-label"
                >Controlled:
            </label>
            <div class="col-8 px-2 col-input">
                <b-select
                    v-model="data.control"
                    class="form-control py-0"
                    name="control"
                >
                    <option :value="1">Yes</option>
                    <option :value="0">No</option>
                    >
                </b-select>
            </div>
        </div>

        <!-- UPDATE = 3 (CHANGE FEES) -->
        <div class="form-group row" v-if="data.update == UPDATES.CHANGE_FEES">
            <label for="fees" class="col-4 col-form-label">Fees: </label>
            <div class="col-8 px-2 col-input">
                <vet-select-fee
                    :value="data.fees"
                    @input="val => (data.fees = val)"
                ></vet-select-fee>
            </div>
        </div>

        <!-- UPDATE = 4 (CHANGE NEXT DUE) -->
        <div
            class="form-group row"
            v-if="data.update == UPDATES.CHANGE_NEXT_DUE"
        >
            <label for="due" class="col-4 col-form-label">Next Due: </label>
            <div class="col-8 px-2 col-input">
                <b-select
                    v-model="data.due"
                    class="form-control py-0"
                    name="due"
                >
                    <option
                        v-for="option in duesOptions"
                        :value="option.id"
                        :key="option.id"
                    >
                        {{ option.name }}</option
                    >
                </b-select>
            </div>
        </div>

        <!-- UPDATE = 5 (CHANGE PRINT ON INVOICE) -->
        <div class="form-group row" v-if="data.update == UPDATES.UPDATE_POI">
            <label for="isPrintOnInvoice" class="col-4 col-form-label"
                >Print on invoice:
            </label>
            <div class="col-8 px-2 col-input">
                <b-select
                    v-model="data.isPrintOnInvoice"
                    class="form-control"
                    name="isPrintOnInvoice"
                >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                </b-select>
            </div>
        </div>

        <!-- UPDATE = 9 (INCREASE PRICE) -->
        <div
            class="form-group row"
            v-if="data.update == UPDATES.INCREASE_PRICE"
        >
            <label for="increasePrice" class="col-4 col-form-label"
                >Increase by:
            </label>
            <div class="col-8 px-2 col-input">
                <div class="row">
                    <div class="col-8 pr-1">
                        <b-input
                            v-model="data.increasePrice"
                            type="text"
                            placeholder="Increase by (required)"
                            name="increasePrice"
                            class="form-control"
                            :class="{
                                'border-danger': errors.has('increasePrice')
                            }"
                            v-validate="
                                'required|numericalWithDecimal|min_value:0|decimal:2'
                            "
                        />
                    </div>
                    <div class="col-4 pl-1 align-self-center">
                        %
                    </div>
                </div>
            </div>
        </div>

        <!-- UPDATE = 10 (SET PRICE) -->
        <div class="form-group row" v-if="data.update == UPDATES.SET_PRICE">
            <label for="setPrice" class="col-4 col-form-label"
                >Base Price:
            </label>
            <div class="col-8 px-2 col-input">
                <div class="row">
                    <div class="col-4 pr-1">
                        <b-input
                            v-model="data.price"
                            type="number"
                            placeholder="Base Price"
                            name="price"
                            class="form-control"
                            v-validate="
                                'required|numericalWithDecimal|decimal:2'
                            "
                        />
                    </div>
                    <div class="col-8">
                        <b-select
                            v-model="data.costType"
                            class="form-control py-0"
                            name="cost_type"
                        >
                            <option
                                v-for="option in COST_TYPE"
                                :value="option.value"
                                :key="option.value"
                            >
                                {{ option.text }}</option
                            >
                        </b-select>
                    </div>
                </div>
            </div>
        </div>

        <!-- WIP: USE V-SHOW INSTEAD for performance to prevent too many endpoints run on action-form-->
        <!-- WILL LOOK INTO THIS LATER AS IT CHANGE FLOW -->
        <!-- UPDATE = 7 (CREATE ACTION) -->
        <Action-Form
            v-if="data.update == UPDATES.CREATE_ACTION"
            ref="action-form"
            module="BULK_UPDATE"
            @show-cert-alert="
                showCertAlert => {
                    showCertWarning = showCertAlert;
                }
            "
            :isDisableForDisabled="isDisableForDisabled"
            :bundlesBulkUpdate="bundles"
            :loadingBundlesBulkUpdate="optionLoading.bundle"
        />

        <!-- 
            UPDATE = 10 (REMOVE ACTION) 
        -->
        <div class="form-group row" v-if="data.update == UPDATES.REMOVE_ACTION">
            <label for="bundleOptions" class="col-4 col-form-label"
                >Update Bundles:
            </label>
            <div class="col-8 px-2 col-input">
                <b-select
                    id="bundleOptions"
                    name="bundleOptions"
                    v-model="data.bundleOptions"
                    class="form-control"
                >
                    <option
                        v-for="opt in bundleOptions"
                        :key="opt.key"
                        :value="opt.key"
                        >{{ opt.value }}
                    </option>
                </b-select>
            </div>
        </div>
        <div
            class="form-group row"
            v-if="
                data.update == UPDATES.REMOVE_ACTION && data.bundleOptions == 2
            "
        >
            <label for="bundles" class="col-4 col-form-label"></label>
            <div class="col-8 px-2 col-input">
                <v-select
                    id="bundles"
                    name="bundles"
                    v-model="data.bundles"
                    :options="bundlesFiltered"
                    multiple
                >
                    <div slot="option" slot-scope="option">
                        <span style="white-space: normal;">
                            {{ option.label }}
                        </span>
                    </div>
                    <template slot="no-options">
                        <span> Type to search / No matches found </span>
                    </template>
                </v-select>
            </div>
        </div>

        <div class="form-group row" v-if="data.update == UPDATES.REMOVE_ACTION">
            <label for="removeAction" class="col-4 col-form-label"
                >Action:
            </label>
            <div class="col-8 px-2 col-input">
                <v-select
                    id="removeAction"
                    name="removeAction"
                    v-model="data.removeAction"
                    :disabled="actions.length == 0"
                    :selectOnTab="true"
                    :options="actions"
                    :placeholder="
                        actions.length == 0
                            ? 'No Action Available'
                            : 'Select action (required)'
                    "
                    v-validate="'required'"
                    :class="{
                        'border-danger': errors.has('removeAction')
                    }"
                >
                    <template slot="no-options">
                        <span> No Action Available </span>
                    </template>
                </v-select>
            </div>
        </div>
        <template v-slot:modal-footer>
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading"
                variant="primary"
                @click="savingCheckDue"
                :disabled="
                    data.update == UPDATES.REMOVE_ACTION && actions.length == 0
                "
            >
                Update
            </b-button>
        </template>
    </b-modal>
</template>

<script>
/**
 * @name BulkUpdate
 * @see apps/application/controllers/inventory/bulk_update.php
 * @see apps/assets/js/module/inventory/inventory.js
 */
import ItemService from "@/services/items";
import ItemActionService from "@/services/items/items-action";
import { differenceBy, forEach, map, filter, find, sortBy } from "lodash";
import ItemMixin from "@/mixins/item.js";
import CustomRulesVeeValidate from "@/mixins/custom-rules-veevalidate";
import ACTIONS from "@/constants/actions";
import ItemServices from "@/services/items";

const UPDATES = {
    CHANGE_CONTROLLED_STATUS: 2,
    CHANGE_FEES: 3,
    CHANGE_NEXT_DUE: 4,
    UPDATE_POI: 6,
    CREATE_ACTION: 7,
    INCREASE_PRICE: 9,
    REMOVE_ACTION: 8,
    SET_PRICE: 10
};

const BUNDLE_OPTIONS = [
    { key: 1, value: "Update all bundles" },
    { key: 2, value: "Update specific bundles" },
    { key: 3, value: "Do not update bundles" }
];

export default {
    mixins: [ItemMixin, CustomRulesVeeValidate],
    components: {
        Loader: () => import("@/module/commons/loader"),
        vSelect: () => import("vue-select"),
        Treeselect: () => import("@riophae/vue-treeselect"),
        vetSelectFee: () => import("@/module/commons/vet-select-fee"),
        ActionForm: () => import("@/components/item-actions/action-form.vue")
    },
    data() {
        return {
            modalId: "InventoryBulkUpdate",
            UPDATES: UPDATES,
            BUNDLE_OPTIONS: BUNDLE_OPTIONS,
            isOpen: false,
            isLoading: false,
            queryItem: null,
            queryBundle: null,
            items: [],
            categories: [],
            bundles: [],
            actions: [],
            formError: [],
            bundleOptions: [],
            optionLoading: {
                items: true,
                action: false
            },
            data: {
                applyTo: 0,
                applyToItems: [],
                applyToCategories: [],
                update: 0,
                control: 1,
                due: "0",
                isPrintOnInvoice: "1",
                fees: [],
                increasePrice: 0,
                price: 0,
                costType: 1,
                bundleOptions: 1,
                bundles: [],
                removeAction: null
            },
            showCertWarning: false
        };
    },
    computed: {
        itemsFiltered() {
            return differenceBy(this.items, this.data.applyToItems, "id");
        },
        bundlesFiltered() {
            return differenceBy(this.bundles, this.data.bundles, "id");
        },
        isRenderBundleAPI() {
            return this.data.update == 7 || this.data.update == 8;
        },
        isRenderActionAPI() {
            return this.data.update == 8;
        },
        isDisableForDisabled() {
            return this.data.applyTo == 0
                ? this.data.applyToItems.length == 0
                : this.data.applyToCategories.length == 0;
        }
    },
    watch: {
        "data.applyTo"() {
            this.bundles = [];
            this.data.bundles = [];
            this.renderAPI();
        },
        "data.update"(value) {
            this.renderAPI();
        },
        "data.bundleOptions"(value, oldValue) {
            if (oldValue == 3) {
                this.getBundlesBulkUpdate();
            }
        },
        "data.applyToItems"(values) {
            this.renderAPI();
            this.emptyDisableFor(values.length);
        },
        "data.applyToCategories"(values) {
            this.renderAPI();
            this.emptyDisableFor(values.length);
        }
    },
    methods: {
        open() {
            this.isOpen = true;
            this.getItems();
            this.getCategories();
            this.getDuesOptions();
            this.getBundleOptions();
        },
        close() {
            this.isOpen = false;
        },
        updateSelectedCategory(value) {
            if (value) {
                this.clearError("category");
            }
        },
        searchItem(text) {
            if (text.length >= 2) {
                this.searching(text, this);
            }
        },
        searching: _.debounce(async (text, self) => {
            self.queryItem = text;
            self.getItems();
        }, 300),
        renderAPI() {
            if (this.isRenderBundleAPI) {
                this.getBundlesBulkUpdate();
            }

            if (this.isRenderActionAPI) {
                this.getActionOptions();
            }
        },
        emptyDisableFor(selectedCounts) {
            if (
                this.data.update == UPDATES.CREATE_ACTION &&
                this.$refs["action-form"] &&
                selectedCounts == 0
            ) {
                this.$refs["action-form"].action.disableForItem = [];
                this.$refs["action-form"].action.disableForCategory = [];
            }
        },
        /////////////////////////////////
        // RETRIEVING DATA FOR OPTIONS //
        /////////////////////////////////
        getCategories() {
            let self = this;
            self.typesArray = [];
            ItemService.getCategories().then(res => {
                forEach(res, function (type) {
                    let categories = [];
                    if (type.category.length > 0) {
                        type.category = sortBy(type.category, "name");
                        forEach(type.category, function (category) {
                            let categoryObj = {
                                id: category.id,
                                label: category.name,
                                typeId: category.item_type_id,
                                classId: type.item_class_id
                            };
                            categories.push(categoryObj);
                        });

                        self.typesArray.push({
                            id: "type" + type.id,
                            label: type.value,
                            children: categories
                        });
                    }
                });
            });
        },
        async getItems() {
            let self = this;
            self.optionLoading.items = true;
            self.items = [];

            const res = await ItemService.bulkUpdateOptions({
                apply_to: 0,
                search_key: this.queryItem
            });

            self.optionLoading.items = false;

            const items = res.resources.items;
            Object.keys(items).forEach(keyItem => {
                self.items.push({
                    id: keyItem,
                    label: items[keyItem]
                });
            });
        },
        /**
         * @method getBundleOptions
         * @description get bundles options
         */
        getBundleOptions() {
            let self = this;
            self.bundleOptions = [];
            ItemActionService.getBundleOptions().then(res => {
                let serverData = res.resources.data;
                Object.keys(serverData).forEach(key => {
                    self.bundleOptions.push({
                        key: key,
                        value: serverData[key]
                    });
                });
            });
        },
        /**
         * @method getBundlesSelection
         * @description get bundles list
         */
        getBundlesBulkUpdate() {
            // based on array of item ids or categories
            let self = this;
            self.bundles = [];
            self.optionLoading.bundle = true;

            ItemServices.bulkUpdateOptions({
                apply_to: this.data.applyTo,
                update_bundle: 2,
                item_ids:
                    self.data.applyTo == 0
                        ? self.data.applyToItems.map(i => i.id)
                        : self.data.applyToCategories
            }).then(res => {
                self.optionLoading.bundle = false;
                if (res && res.resources && res.resources.bundles) {
                    self.bundles = map(res.resources.bundles, (label, id) => {
                        return {
                            id,
                            label
                        };
                    });
                    self.data.bundles = self.bundles;
                }
            });
        },
        /**
         * @method getActionOptions
         * @description get bundles list
         */
        getActionOptions() {
            // based on array of item ids or categories
            let self = this;
            self.actions = [];
            self.optionLoading.action = true;

            ItemServices.bulkUpdateOptions({
                apply_to: this.data.applyTo,
                only_action: 1,
                update_option: UPDATES.REMOVE_ACTION,
                item_ids:
                    self.data.applyTo == 0
                        ? self.data.applyToItems.map(i => i.id)
                        : self.data.applyToCategories
            }).then(res => {
                self.optionLoading.action = false;
                if (res && res.resources && res.resources.action) {
                    if (self.data.update == UPDATES.REMOVE_ACTION) {
                        // Fixed Issue VET-933, consolidate same action name but the id would be in array of the action
                        let consolidatedActions = [];
                        Object.keys(res.resources.action).forEach((id, idx) => {
                            let act = res.resources.action[id];
                            let sameAct = find(consolidatedActions, [
                                "label",
                                act
                            ]);
                            if (sameAct) {
                                let idArray = sameAct.id.split(",");
                                idArray.push(id);
                                sameAct.id = idArray.toString();
                            } else {
                                consolidatedActions.push({
                                    id: id,
                                    label: act
                                });
                            }
                        });
                        self.actions = consolidatedActions;
                    } else {
                        self.actions = map(
                            res.resources.action,
                            (label, id) => {
                                return {
                                    id,
                                    label
                                };
                            }
                        );
                    }
                }
            });
        },
        ////////////
        // SAVING //
        ////////////
        /**
         * @method checkFormIsValid
         * @description To check is form dont have any error
         * @return  {Boolean}
         */
        checkFormIsValid() {
            let isValid = !(this.errors.items.length > 0);
            if (
                this.data.applyTo == 1 &&
                this.data.applyToCategories.length == 0
            ) {
                this.formError.push("applyToCategories");
                isValid = false;
            } else if (
                this.data.applyTo == 0 &&
                this.data.applyToItems.length == 0
            ) {
                this.formError.push("applyToItems");
                isValid = false;
            }

            return isValid;
        },
        /**
         * @method generateParams
         * @see apps/Users/vsdev/www/apps/application/controllers/inventory/bulk_update.php
         */
        generateParams() {
            let self = this;

            const actionInput = this.$refs["action-form"].action;
            const selectedBundles = map(
                actionInput.bundles,
                bundle => bundle.id
            );

            let params = {
                action: parseInt(actionInput.action.id),
                note: actionInput.note,
                bundles_options: parseInt(actionInput.bundleOption),
                bundles_count: selectedBundles.length,
                bundles: selectedBundles
            };

            switch (params.action) {
                case ACTIONS.ID.CHANGE_PATIENT_STATUS:
                    params.patient_status = parseInt(actionInput.changeStatus);
                    params.reminder = parseInt(
                        actionInput.changeStatusReminder
                    );
                    break;
                case ACTIONS.ID.CREATE_REMINDER:
                    params.reminder_type = parseInt(actionInput.reminderType);
                    if (
                        [
                            self.REMINDER_TYPE.EMAIL,
                            self.REMINDER_TYPE.SMS
                        ].includes(params.reminder_type)
                    ) {
                        params.reminder_send = parseInt(
                            actionInput.reminderSend
                        );
                        params.reminder_send_period = parseInt(
                            actionInput.reminderSendPeriod
                        );
                        params.reminder_send_option = parseInt(
                            actionInput.reminderSendOption
                        );
                    } else {
                        /**
                         * @see application/controllers/ajax/item.php Line:1444
                         */
                        params.reminder_send = 2;
                        params.reminder_send_period = 4;
                        params.reminder_send_option = 1;
                    }
                    break;
                case ACTIONS.ID.CREATE_TASK: {
                    let generatedAssignTos = {};
                    const staffIds = filter(actionInput.taskAssignees, {
                        type: "staff"
                    });
                    if (staffIds.length > 0) {
                        generatedAssignTos.staffs = map(staffIds, s => {
                            return { id: s.id };
                        });
                    }

                    const roleIds = filter(actionInput.taskAssignees, {
                        type: "role"
                    });
                    if (roleIds.length > 0) {
                        generatedAssignTos.roles = map(roleIds, r => {
                            return { id: r.id };
                        });
                    }

                    if (
                        find(actionInput.taskAssignees, {
                            type: "provider"
                        })
                    ) {
                        params.forProvider = true;
                    }
                    params.assignTos = generatedAssignTos;
                    params.due = parseInt(actionInput.taskDue);
                    params.taskDueFrequency = parseInt(
                        actionInput.taskDueFrequency
                    );
                    params.time = Math.round(actionInput.taskTime.toSeconds());
                    params.content = actionInput.task;

                    break;
                }
                case ACTIONS.ID.CREATE_ALERT:
                    params.alert_type = parseInt(actionInput.alertType);
                    params.when = actionInput.alertWhen;
                    break;
                case ACTIONS.ID.PRINT_CERTIFICATE:
                    params.cert_type = actionInput.printCertType;
                    params.printer = actionInput.printPrinter;
                    params.provider_id = self.$root.currentActor.id;
                    break;
                case ACTIONS.ID.DISABLE_REMINDER:
                    params.disable_for = actionInput.disableDisableFor;
                    if (params.disable_for == 0) {
                        params.items = map(
                            actionInput.disableForItem,
                            i => i.id
                        );
                    } else {
                        params.categoryIds = actionInput.disableForCategory;
                        params.items = [];
                    }
                    break;
                case ACTIONS.ID.CREATE_LETTER:
                    params.letters = map(
                        actionInput.letters,
                        letter => letter.id
                    ).join(",");
                    break;
                case ACTIONS.ID.CREATE_FORM:
                    params.webforms = map(
                        actionInput.forms,
                        form => form.id
                    ).join(",");
                    break;
                case ACTIONS.ID.ADD_ATTACHMENT: {
                    if (
                        actionInput.attachmentSource ==
                        self.ATTACHMENT_OPTION.ATTACHMENT_LIFELEARN
                    ) {
                        params.articleData = {
                            sourceAttachment: actionInput.attachmentSource,
                            article: map(
                                actionInput.attachmentArticles,
                                article => {
                                    return {
                                        articleID: article.id,
                                        title: article.title
                                    };
                                }
                            )
                        };
                    } else {
                        params.attachments = actionInput.attachmentTemplates.map(
                            template => template.id
                        );
                    }
                    break;
                }
                default:
                    break;
            }
            return params;
        },
        /**
         * @method savingCheckDue
         * @description check whether to prompt update warning message when update item due to none
         */
        savingCheckDue() {
            if (
                this.data.update == UPDATES.CHANGE_NEXT_DUE &&
                Number(this.data.due) == 0
            ) {
                this.$emit("show-warning-modal");
                return;
            }
            this.proceed();
        },
        async proceed() {
            let self = this;
            let isActionFormValid = true;
            const validateValid = await this.$validator.validateAll();

            if (
                self.data.update == UPDATES.CREATE_ACTION &&
                self.$refs["action-form"]
            ) {
                isActionFormValid = await self.$refs[
                    "action-form"
                ].checkFormIsValid();
            }

            if (
                !self.checkFormIsValid() ||
                !isActionFormValid ||
                !validateValid
            ) {
                return false;
            }

            try {
                self.isLoading = true;

                let params = {
                    apply_to: self.data.applyTo,
                    update: self.data.update,
                    update_ids:
                        self.data.applyTo == 0
                            ? self.data.applyToItems.map(i => i.id).join(",")
                            : self.data.applyToCategories.join(",")
                };

                let selectedBundlesId = [];

                switch (self.data.update) {
                    case UPDATES.CHANGE_CONTROLLED_STATUS:
                        params.update_columns = {
                            control: this.data.control
                        };
                        break;
                    case UPDATES.CHANGE_FEES:
                        params.update_columns = {
                            fees: this.data.fees.map(f => f.id)
                        };
                        break;
                    case UPDATES.CHANGE_NEXT_DUE:
                        params.update_columns = {
                            due: this.data.due
                        };
                        break;
                    case UPDATES.UPDATE_POI:
                        params.update_columns = {
                            is_print_on_invoice: this.data.isPrintOnInvoice
                        };
                        break;
                    case UPDATES.CREATE_ACTION:
                        params.update_columns = this.generateParams();
                        break;
                    case UPDATES.INCREASE_PRICE:
                        params.update_columns = {
                            increase_by: this.data.increasePrice
                        };
                        break;
                    case UPDATES.REMOVE_ACTION:
                        selectedBundlesId = map(
                            this.data.bundles,
                            bundle => bundle.id
                        );

                        params.update_columns = {
                            action_item_ids: this.data.removeAction.id,
                            bundles_options: parseInt(this.data.bundleOptions),
                            bundles_count: selectedBundlesId.length,
                            bundles: selectedBundlesId
                        };

                        break;
                    case UPDATES.SET_PRICE:
                        params.update_columns = {
                            price: this.data.price,
                            cost_type: this.data.costType
                        };
                        break;
                }

                const res = await ItemService.postBulkUpdate(params);
                if (res && res.message && res.message.code == 200) {
                    this.isLoading = false;
                    this._message("Update completed successfully");

                    this.close();
                    // reload quick edit if only the current tab is in quick edit
                    this.$emit("onsuccess");
                } else {
                    if (res && res.message && res.message.errors) {
                        console.error(res.message.errors);
                    }
                    throw new Error("Error: Cannot bulk update");
                }
            } catch (error) {
                this.isLoading = false;
                this._message(error.message, "danger");
            }
        },
        /**
         * @method clearError
         * @description To remove the error field from this.formError
         * @param  {String} key
         */
        clearError(key) {
            this.formError = this.formError.filter(err => err !== key);
        }
    }
};
</script>

<style>
/**
* @see https://github.com/riophae/vue-treeselect/issues/137 
*/
.vue-treeselect.treeselect-invalid:not(.vue-treeselect--open)
    .vue-treeselect__control {
    border-color: #b94a48 !important;
    background-color: #ffdddd !important;
}
</style>
